import { flattenSchoolProfileRecord } from "@/project/karuna/utils/fetchSchemaGridRecords";
import { getSchoolRecordId } from "@/project/karuna/utils/schoolProfileApproval";

/** Read a field from grid rows that may nest values under basicGrid, basicInfo, or data. */
export function extractSchoolProfileField(
  record: Record<string, unknown>,
  possibleNames: string[]
): string {
  let parsedData: unknown = record?.data;
  if (typeof parsedData === "string") {
    try {
      parsedData = JSON.parse(parsedData) as Record<string, unknown>;
    } catch {
      parsedData = undefined;
    }
  }

  const recordSources: Record<string, unknown>[] = [
    record,
    parsedData as Record<string, unknown>,
    record.basicInfo as Record<string, unknown>,
    record.basic_info as Record<string, unknown>,
    record.basic_grid as Record<string, unknown>,
    record.basicGrid as Record<string, unknown>,
    (parsedData as Record<string, unknown> | undefined)?.basicInfo,
    (parsedData as Record<string, unknown> | undefined)?.basic_info,
    (parsedData as Record<string, unknown> | undefined)?.basic_grid,
    (parsedData as Record<string, unknown> | undefined)?.basicGrid,
  ].filter(
    (source): source is Record<string, unknown> =>
      source !== null &&
      source !== undefined &&
      typeof source === "object" &&
      !Array.isArray(source)
  );

  for (const source of recordSources) {
    for (const name of possibleNames) {
      const value = source[name];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return String(value).trim();
      }

      const lowerName = name.toLowerCase();
      for (const key of Object.keys(source)) {
        if (key.toLowerCase() === lowerName) {
          const nestedValue = source[key];
          if (
            nestedValue !== undefined &&
            nestedValue !== null &&
            String(nestedValue).trim() !== ""
          ) {
            return String(nestedValue).trim();
          }
        }
      }
    }
  }

  for (const root of [record, parsedData as Record<string, unknown>]) {
    if (!root || typeof root !== "object" || Array.isArray(root)) {
      continue;
    }
    for (const key of Object.keys(root)) {
      if (!key.includes(".")) {
        continue;
      }
      const last = key.split(".").pop() ?? "";
      for (const name of possibleNames) {
        if (last.toLowerCase() !== name.toLowerCase()) {
          continue;
        }
        const value = root[key];
        if (
          value !== undefined &&
          value !== null &&
          String(value).trim() !== ""
        ) {
          return String(value).trim();
        }
      }
    }
  }

  // Deep search nested objects (e.g. basicGrid only on getItemById payload).
  const visited = new Set<object>();
  const walk = (node: unknown, depth: number): string => {
    if (
      depth > 6 ||
      node === null ||
      node === undefined ||
      typeof node !== "object" ||
      Array.isArray(node)
    ) {
      return "";
    }
    if (visited.has(node)) {
      return "";
    }
    visited.add(node);

    const obj = node as Record<string, unknown>;
    for (const name of possibleNames) {
      const value = obj[name];
      if (
        value !== undefined &&
        value !== null &&
        String(value).trim() !== ""
      ) {
        return String(value).trim();
      }
      const lowerName = name.toLowerCase();
      for (const key of Object.keys(obj)) {
        if (key.toLowerCase() === lowerName) {
          const nestedValue = obj[key];
          if (
            nestedValue !== undefined &&
            nestedValue !== null &&
            String(nestedValue).trim() !== ""
          ) {
            return String(nestedValue).trim();
          }
        }
      }
    }

    for (const value of Object.values(obj)) {
      const found = walk(value, depth + 1);
      if (found) {
        return found;
      }
    }

    return "";
  };

  const deepMatch = walk(record, 0);
  if (deepMatch) {
    return deepMatch;
  }

  return "";
}

export interface SchoolDisplayFields {
  kc_no: string;
  school_name: string;
  principal_name: string;
  contact_number: string;
  email: string;
  kendra_name: string;
  chapter_id?: string;
}

export function mapSchoolRecordToDisplayFields(
  record: Record<string, unknown>
): SchoolDisplayFields {
  const field = (names: string[], fallback: string) =>
    extractSchoolProfileField(record, names) || fallback;

  const chapterId = extractSchoolProfileField(record, [
    "chapter_id",
    "chapterid",
  ]);

  return {
    kc_no: field(["kcno", "kc_no"], "N/A"),
    school_name: field(["schoolname", "school_name"], "Unknown School"),
    principal_name: field(
      ["principal", "principalname", "principal_name"],
      "N/A"
    ),
    contact_number: field(
      [
        "contact",
        "schoolcontact",
        "school_contact",
        "contact_number",
        "contactfax",
        "contact_fax",
        "mobileno",
        "mobile_no",
      ],
      "N/A"
    ),
    email: field(["schoolemail", "school_email", "email", "emailid"], "N/A"),
    kendra_name: field(["kendra", "nearestkendra", "kendra_name"], "N/A"),
    chapter_id: chapterId || undefined,
  };
}

export function mapSchoolGridRecordToListItem(
  record: Record<string, unknown>
): ({ id: string } & SchoolDisplayFields) | null {
  const normalized = flattenSchoolProfileRecord(record);
  const schoolId = getSchoolRecordId(normalized);
  if (!schoolId) {
    return null;
  }

  return {
    id: schoolId,
    ...mapSchoolRecordToDisplayFields(normalized),
  };
}
